import { positions } from "react-alert";
import hubAPI from "../services/hubAPI";
import { isEqual } from "lodash";

export const generateUID = () => {
  return "xxxxxxxx".replace(/[xy]/g, function (c) {
    var r = (Math.random() * 16) | 0,
      v = c == "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export const generateLongUID = () => {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    var r = (Math.random() * 16) | 0,
      v = c == "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export const pickTextColorBasedOnBgColorSimple = (
  bgColor,
  lightColor,
  darkColor,
) => {
  var color = bgColor.charAt(0) === "#" ? bgColor.substring(1, 7) : bgColor;
  var r = parseInt(color.substring(0, 2), 16); // hexToR
  var g = parseInt(color.substring(2, 4), 16); // hexToG
  var b = parseInt(color.substring(4, 6), 16); // hexToB
  return r * 0.299 + g * 0.587 + b * 0.114 > 186 ? darkColor : lightColor;
};

export const colorHexToRgb = (hex) => {
  var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? [
        parseInt(result[1], 16),
        parseInt(result[2], 16),
        parseInt(result[3], 16),
      ]
    : [0, 0, 0];
};

export const extractNumberSeparators = (culture) => {
  const baseNumber = 1000.01;
  const parts = Intl.NumberFormat(culture || "en-US").formatToParts(baseNumber);
  const separators = {
    decimalSeparator: parts.find((p) => p.type === "decimal")?.value || ".",
    groupSeparator: parts.find((p) => p.type === "group")?.value || ",",
  };
  return separators;
};

export const extractNumberCurrencySeparators = (culture, currency) => {
  const baseNumber = 1000.01;
  const parts = Intl.NumberFormat(culture || "en-US", {
    style: "currency",
    currency: currency || "USD",
  }).formatToParts(baseNumber);
  const separators = {
    decimalSeparator: parts.find((p) => p.type === "decimal")?.value || ".",
    groupSeparator: parts.find((p) => p.type === "group")?.value || ",",
    symbol: parts.find((p) => p.type === "currency")?.value || "$",
  };
  return separators;
};

export const parseLocaleNumber = (value, culture) => {
  const separators = extractNumberSeparators(culture);

  const stringValue = value
    .replace(new RegExp("\\" + separators.groupSeparator, "g"), "")
    .replace(new RegExp("\\" + separators.decimalSeparator, "g"), ".");

  if (isNaN(stringValue)) return 0;

  return parseFloat(stringValue);
};

export const parseLocaleNumberCurrency = (value, culture, currency) => {
  const separators = extractNumberCurrencySeparators(culture, currency);

  const stringValue = value
    .replace(separators.symbol, "")
    .replace(/\s/g, "")
    .replace(new RegExp("\\" + separators.groupSeparator, "g"), "")
    .replace(new RegExp("\\" + separators.decimalSeparator, "g"), ".");

  if (isNaN(stringValue)) return 0;

  return parseFloat(stringValue);
};

export const checkIsNaNWithCulture = (value, culture) => {
  const separators = extractNumberSeparators(culture);

  const stringValue = value
    .replace(new RegExp("\\" + separators.groupSeparator, "g"), "")
    .replace(new RegExp("\\" + separators.decimalSeparator, "g"), ".");

  return isNaN(stringValue);
};

export const formatLabel = (label, context) => {
  const baseNumber = 1000.01;
  const { filterCatalogs, company } = context;

  //Get currency symbol
  const currencyFormatter = new Intl.NumberFormat(context?.locale || "en-US", {
    style: "currency",
    currency: company?.currencyID || "USD",
  });
  const parts = currencyFormatter.formatToParts(baseNumber);
  const currencySymbol = parts.find((p) => p.type === "currency").value || "$";
  label = label.replace("@currency", currencySymbol);

  //Get measure length
  const measureLengthSymbol =
    (filterCatalogs?.measures || []).find(
      (f) => f.measureID === (company?.lengthMeasureID || 200),
    )?.symbol || "Km";
  label = label.replace("@measureLength", measureLengthSymbol);

  //Get measure volume
  const measureVolumeSymbol =
    (filterCatalogs?.measures || []).find(
      (f) => f.measureID === (company?.volumeMeasureID || 300),
    )?.symbol || "L";
  label = label.replace("@measureVolume", measureVolumeSymbol);

  return label;
};

export const getDataLayerNormalizedString = (value) => {
  return value
    .trim()
    .replace(/ /g, "_")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9_]/g, "")
    .toLowerCase();
};

export const parseUrl = (url, args) => {
  const regex = /{(\w+)(:\w+)?(-\w+)?}/g;
  //const regex = /{(\w+)(:[a-zA-Z_0-9]|[0-9]|_|-)?}/g;
  //const regex = /{(\w+)(:[a-zA-Z_0-9-])?}/g;

  let match = regex.exec(url);
  if (match === null) return url;
  const key = match[1];
  const keyValue = match[2]?.replace(":", "") || "";

  if (key.toLowerCase() === "dashboardKey".toLowerCase()) {
    const dashboardKey = `${keyValue}${
      match[3] === "-v1" || match[3] === "-v2" ? match[3] : ""
    }`;
    const dashboardID =
      (args?.dashboards || []).find(
        (x) => x.dashboardKey?.toLowerCase() === dashboardKey?.toLowerCase(),
      )?.dashboardID || 0;
    url = url.replace(match[0], dashboardID);
  } else url = url.replace(match[0], args[key] || "");

  return parseUrl(url, args);
};

//US43662: Almacenar filtros y grupos en storage local
export const setTempFilter = (key, value) => {
  const storageKey = "user-settings";

  const baseObject = { tempFilters: [], hardFilters: [], settings: [] };
  let userSettings = localStorage.getItem(storageKey) || baseObject;
  if (typeof userSettings === "string") {
    try {
      userSettings = JSON.parse(userSettings);
    } catch (_) {
      userSettings = baseObject;
    }
  }

  userSettings.tempFilters = userSettings.tempFilters.filter(
    (x) => x[0] !== key,
  );
  userSettings.tempFilters = [...userSettings.tempFilters, [key, value]];

  localStorage.setItem(storageKey, JSON.stringify(userSettings));
};

//US43662: Almacenar filtros y grupos en storage local
export const getTempFilter = (key) => {
  const storageKey = "user-settings";

  const baseObject = { tempFilters: [], hardFilters: [], settings: [] };
  let userSettings = localStorage.getItem(storageKey) || baseObject;
  if (typeof userSettings === "string") {
    try {
      userSettings = JSON.parse(userSettings);
    } catch (_) {
      userSettings = baseObject;
    }
  }

  var element = userSettings.tempFilters.find((x) => x[0] === key);
  return !element ? null : element[1];
};

//US43662: Almacenar filtros y grupos en storage local
export const setHardFilter = (key, value) => {
  const storageKey = "user-settings";

  const baseObject = { tempFilters: [], hardFilters: [], settings: [] };
  let userSettings = localStorage.getItem(storageKey) || baseObject;
  if (typeof userSettings === "string") {
    try {
      userSettings = JSON.parse(userSettings);
    } catch (_) {
      userSettings = baseObject;
    }
  }

  userSettings.hardFilters = userSettings.hardFilters.filter(
    (x) => x[0] !== key,
  );
  userSettings.hardFilters = [...userSettings.hardFilters, [key, value]];

  var bodyFormData = new FormData();
  bodyFormData.append("key", key);
  bodyFormData.append("value", JSON.stringify(value));
  hubAPI
    .post(
      `api/v1/User/${localStorage.getItem("userId") || "?"}/usersettings`,
      bodyFormData,
    )
    .then((_) => {})
    .catch((error) => {
      console.log(error);
    });

  localStorage.setItem(storageKey, JSON.stringify(userSettings));
};

//US43662: Almacenar filtros y grupos en storage local
export const getHardFilter = (key) => {
  const storageKey = "user-settings";

  const baseObject = { tempFilters: [], hardFilters: [], settings: [] };
  let userSettings = localStorage.getItem(storageKey) || baseObject;
  if (typeof userSettings === "string") {
    try {
      userSettings = JSON.parse(userSettings);
    } catch (_) {
      userSettings = baseObject;
    }
  }

  var element = userSettings.hardFilters.find((x) => x[0] === key);
  return !element ? null : element[1];
};

export const extractFileNameFromUrl = (url, has_uid) => {
  const urlSegments = url.split("/");

  const fileName = decodeURIComponent(urlSegments[urlSegments.length - 1]);
  if (!has_uid) return fileName;

  const readableNameSegments = fileName.split("_");
  return !!readableNameSegments
    ? readableNameSegments[readableNameSegments.length - 1]
    : undefined;
};

export const defaultServiceGroups = [
  { serviceGroupID: 1, name: "Fuel", color: "#F72717" },
  { serviceGroupID: 2, name: "Maintenance", color: "#FF5978" },
  { serviceGroupID: 3, name: "Fines", color: "#66C7B5" },
  { serviceGroupID: 4, name: "Telematics", color: "#F2994A" },
  { serviceGroupID: 5, name: "Rents", color: "#9ED0FF" },
  { serviceGroupID: 6, name: "Tires", color: "#ABC266" },
  { serviceGroupID: 7, name: "Documents", color: "#00A184" },
  { serviceGroupID: 8, name: "Wash", color: "#f67376" },
  { serviceGroupID: 9, name: "Parking", color: "#ABC266" },
  { serviceGroupID: 10, name: "Toll", color: "#739A00" },
  { serviceGroupID: 11, name: "Insurance", color: "#711CFF" },
  { serviceGroupID: 12, name: "Acquirements", color: "#0D8AFF" },
  { serviceGroupID: 13, name: "Depreciation", color: "#6EB9FF" },
  { serviceGroupID: 14, name: "Accesories", color: "#f69fa0" },
  { serviceGroupID: 15, name: "Managment tools", color: "#f3be8e" },
];

export const normalizeArray = (dataArray) => {
  const arrayNormalized = dataArray.map((valor) =>
    valor
      ?.normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .toLowerCase(),
  );
  return arrayNormalized;
};

export const onlyLetterAndNumber = (value) => {
  if (typeof value !== "string" || !value) return value;
  return value.replace(/[^a-zA-Z0-9]/g, "");
};

export const alertTopOptions = {
  position: positions.TOP_RIGHT,
  timeout: 5000,
};

const sanitizeFormValue = (val) => {
  // Trata valores nulos, indefinidos ou em branco como 'undefined' para sumirem na comparação
  if (val === null || val === undefined || val === "") return undefined;

  // Normaliza datas Moment para o padrão de comparação YYYY-MM-DD
  if (val && typeof val === "object" && val._isAMomentObject) {
    return val.format("YYYY-MM-DD");
  }
  // Normaliza qualquer ISO timestamp vindo da API para manter apenas YYYY-MM-DD
  if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}T/.test(val)) {
    return val.split("T")[0];
  }
  // Extrai o valor do padrão labelInValue do Ant Design
  if (val && typeof val === "object" && "value" in val && !Array.isArray(val)) {
    return sanitizeFormValue(val.value);
  }

  // Processa arrays e descarta se estiverem vazios (ex: MultiSelects esvaziados)
  if (Array.isArray(val)) {
    const arr = val.map(sanitizeFormValue).filter((v) => v !== undefined);
    return arr.length > 0 ? arr : undefined;
  }

  // Processa objetos aninhados e descarta chaves inúteis
  if (typeof val === "object") {
    const obj = {};
    for (const k in val) {
      if (k.startsWith("_")) continue; // Ignora variáveis virtuais/UI (ex: _edited, _vehicleRestrictionExempt)
      const cleaned = sanitizeFormValue(val[k]);
      if (cleaned !== undefined) obj[k] = cleaned;
    }
    return Object.keys(obj).length > 0 ? obj : undefined;
  }

  // Trata e normaliza strings numéricas (evita falso-positivo "0." contra "0" ou "0.00" contra "0")
  if (typeof val === "string" && !isNaN(val) && val.trim() !== "") {
    // Para evitar que IDs ou códigos com zeros à esquerda (ex: "0123") percam o zero,
    // só convertemos para float se tiver ponto decimal ou se o valor original não for alterado significativamente.
    if (val.includes(".") || String(parseFloat(val)) === val) {
      return String(parseFloat(val));
    }
  }

  // Retorna string para evitar falso-positivo 0 === "0"
  return String(val);
};

export const checkFormIsDirty = (currentForm, initialForm) => {
  if (currentForm === initialForm) return false;

  const cleanCurrent = sanitizeFormValue(currentForm) || {};
  const cleanInitial = sanitizeFormValue(initialForm) || {};

  // Valida APENAS as chaves presentes no estado do formulário atual (currentForm original).
  // Ao usar o currentForm não-sanitizado, detectamos campos que foram apagados (que
  // sumiriam no cleanCurrent) sem validar campos extras que a API mandou no initialForm.
  return Object.keys(currentForm || {}).some((key) => {
    if (key.startsWith("_")) return false; // Ignora variáveis de UI do Ant Design
    return !isEqual(cleanCurrent[key], cleanInitial[key]);
  });
};
